import React, { useState } from "react";
import { useOfferApplications, useReviewApplication } from "@/hooks/use-offer-applications";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { ClipboardCheck, Check, X } from "lucide-react";

export function OfferApplicationsPage() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const { data, isLoading } = useOfferApplications({ status: statusFilter || undefined });
  const reviewMutation = useReviewApplication();

  const applications = data?.data ?? [];

  async function handleReview(id: number, status: "approved" | "rejected") {
    const action = status === "approved" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${action} this application?`)) return;
    reviewMutation.mutate({ id, status });
  }

  return (
    <div>
      <PageHeader title="Offer Applications" description="Review affiliate applications for offers" />

      <div className="flex items-center gap-3 mb-4 mt-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <ClipboardCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {statusFilter === "pending" ? "No pending applications" : "No applications found"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Affiliate</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Offer</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Note</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Applied</th>
                {statusFilter === "pending" && (
                  <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {applications.map((app: any) => (
                <tr key={app.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{app.affiliateName}</span>
                    <span className="text-xs text-gray-400 ml-1">#{app.affiliateId}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">{app.offerName}</span>
                    <span className="text-xs text-gray-400 ml-1">#{app.offerId}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={app.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                    {app.note || "-"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(app.createdAt).toLocaleDateString()}
                  </td>
                  {statusFilter === "pending" && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleReview(app.id, "approved")}
                          disabled={reviewMutation.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReview(app.id, "rejected")}
                          disabled={reviewMutation.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
